import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";

const AdminProducts = () => {
  // --- State Management ---
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [categories, setCategories] = useState([]);

  // Form State mapping to the database fields
  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    description: "",
    price: "",
    stock: "",
    image: "", // Stores the existing image name string
    imageFile: null, // Stores the actual file object for uploads
  });

  // API URLs
  const API_URL = `${process.env.REACT_APP_BACKEND}/api/products`;
  const CATEGORY_API_URL = `${process.env.REACT_APP_BACKEND}/api/categories`;

  // --- Lifecycle Methods ---
  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  // --- API Calls ---
  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL);
      setProducts(response.data);
      setError("");
    } catch (err) {
      setError(
        "Failed to fetch products. Please ensure the server is running.",
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(CATEGORY_API_URL);
      setCategories(response.data);
    } catch (err) {
      console.error("Failed to fetch categories:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?"))
      return;
    try {
      await axios.delete(`${API_URL}/${id}`);
      fetchProducts();
    } catch (err) {
      setError("Failed to delete product.");
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create FormData object to handle file upload
    const data = new FormData();
    data.append("name", formData.name);
    data.append("category_id", formData.category_id);
    data.append("description", formData.description);
    data.append("price", formData.price);
    data.append("stock", formData.stock);

    // Append the file if it exists, otherwise append the existing image string
    if (formData.imageFile) {
      data.append("image", formData.imageFile);
    } else {
      data.append("image", formData.image);
    }

    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axios.post(API_URL, data, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      resetForm();
      fetchProducts();
    } catch (err) {
      setError("Failed to save the product.");
      console.error(err);
    }
  };

  // --- Helper Methods ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileChange = (e) => {
    setFormData({ ...formData, imageFile: e.target.files[0] });
  };

  const handleEdit = (product) => {
    setEditId(product.id);
    setFormData({
      name: product.name,
      category_id: product.category_id,
      description: product.description || "",
      price: product.price,
      stock: product.stock,
      image: product.image || "",
      imageFile: null, // Reset file input
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setFormData({
      name: "",
      category_id: "",
      description: "",
      price: "",
      stock: "",
      image: "",
      imageFile: null,
    });
    // Reset file input UI manually if necessary
    const fileInput = document.getElementById("imageUpload");
    if (fileInput) fileInput.value = "";

    setEditId(null);
    setShowForm(false);
    setError("");
  };

  // Helper to determine stock badge color
  const getStockBadge = (stock) => {
    if (stock <= 0)
      return (
        <span className="badge bg-danger rounded-pill px-3 py-2 shadow-sm">
          Out of Stock
        </span>
      );
    if (stock < 15)
      return (
        <span className="badge bg-warning text-dark rounded-pill px-3 py-2 shadow-sm">
          Low Stock ({stock})
        </span>
      );
    return (
      <span className="badge bg-success rounded-pill px-3 py-2 shadow-sm">
        In Stock ({stock})
      </span>
    );
  };

  // Helper to get category name from ID
  const getCategoryName = (id) => {
    const category = categories.find((c) => c.id === id);
    return category ? category.name : `Cat ID: ${id}`;
  };

  // --- Render ---
  return (
    <div
      className="container-fluid px-4 bg-light min-vh-100"
      style={{ paddingTop: "80px", paddingBottom: "40px" }}
    >
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold mb-0 text-dark">Product Management</h2>
          <span className="text-muted small">
            Manage your store inventory and catalog
          </span>
        </div>
        <button
          className={`btn px-4 py-2 fw-semibold shadow-sm ${showForm ? "btn-secondary" : "btn-primary"}`}
          onClick={() => {
            if (showForm) resetForm();
            setShowForm(!showForm);
          }}
        >
          {showForm ? (
            <>
              <i className="bi bi-x-circle me-2"></i>Cancel
            </>
          ) : (
            <>
              <i className="bi bi-plus-circle me-2"></i>Add New Product
            </>
          )}
        </button>
      </div>

      {/* Alert Messages */}
      {error && (
        <div
          className="alert alert-danger alert-dismissible fade show shadow-sm border-0"
          role="alert"
        >
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      {/* Product Form Card */}
      {showForm && (
        <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0 fw-bold text-primary">
              {editId ? "Edit Product Details" : "Create New Product"}
            </h5>
          </div>
          <div className="card-body p-4 bg-white">
            <form onSubmit={handleSubmit} encType="multipart/form-data">
              <div className="row g-4">
                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Product Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control form-control-lg bg-light border-0"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Golden Wildflower Honey"
                    required
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Category <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select form-select-lg bg-light border-0"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="" disabled>
                      Select a category
                    </option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Price ($) <span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-0 text-muted">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control form-control-lg bg-light border-0"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Stock Quantity <span className="text-danger">*</span>
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-lg bg-light border-0"
                    name="stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    placeholder="0"
                    required
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Product Image
                  </label>
                  <input
                    type="file"
                    id="imageUpload"
                    className="form-control form-control-lg bg-light border-0"
                    name="image"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {/* Show existing image name if editing and no new file selected */}
                  {editId && formData.image && !formData.imageFile && (
                    <small className="text-muted d-block mt-1">
                      Current: {formData.image}
                    </small>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold text-secondary small text-uppercase">
                    Description
                  </label>
                  <textarea
                    className="form-control bg-light border-0"
                    name="description"
                    rows="3"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter detailed product description..."
                  ></textarea>
                </div>
              </div>
              <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-success btn-lg px-5 shadow-sm fw-bold"
                >
                  {editId ? "Update Product" : "Save Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Data Table Section */}
      <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th className="text-muted small text-uppercase fw-semibold py-3 ps-4">
                  Product
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3">
                  Category
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3">
                  Price
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3">
                  Inventory
                </th>
                <th className="text-muted small text-uppercase fw-semibold py-3 text-end pe-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No products found. Start by adding a new one.
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id}>
                    <td className="ps-4">
                      <div className="d-flex align-items-center">
                        <div
                          className="bg-light rounded p-2 me-3 text-center d-flex align-items-center justify-content-center overflow-hidden"
                          style={{ width: "50px", height: "50px" }}
                        >
                          {/* OPTIONAL: To preview the image here, configure express.static to serve the uploads folder */}
                          <i className="bi bi-image text-muted fs-4"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold text-dark">
                            {product.name}
                          </h6>
                          <small className="text-muted">
                            ID: #{product.id} • {product.image || "No image"}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 rounded-pill px-3 py-2">
                        {getCategoryName(product.category_id)}
                      </span>
                    </td>
                    <td className="fw-semibold text-dark">
                      ${Number(product.price).toFixed(2)}
                    </td>
                    <td>{getStockBadge(product.stock)}</td>
                    <td className="text-end pe-4">
                      <button
                        className="btn btn-sm btn-light text-primary rounded-circle me-2 shadow-sm"
                        onClick={() => handleEdit(product)}
                        title="Edit Product"
                      >
                        <i className="bi bi-pencil-square"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-light text-danger rounded-circle shadow-sm"
                        onClick={() => handleDelete(product.id)}
                        title="Delete Product"
                      >
                        <i className="bi bi-trash3"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <button className="btn btn-primary m-3">
            <Link to="/categories" className="text-white text-decoration-none">
              Back to Categories
            </Link>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
